import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Briefcase, UserCheck, Building2, Rocket, CheckCircle2 } from 'lucide-react';
import { Testimonials } from '../components/Testimonials';
const services = [{
  icon: Briefcase,
  title: 'C-Suite Search',
  subtitle: 'CEO, COO, CFO, CTO & CIO',
  description: "Identifying and securing transformative leadership for the most critical roles in your organization. We understand that C-level hires define company trajectory — our process is built to find leaders who don't just fill a seat, but reshape what's possible.",
  capabilities: ['Chief Executive Officer searches', 'Chief Operating Officer placement', 'Chief Financial Officer recruitment', 'Chief Technology & Information Officer searches', 'Succession planning for outgoing executives']
}, {
  icon: UserCheck,
  title: 'VP-Level Recruitment',
  subtitle: 'Vice Presidents Across All Functions',
  description: 'Building strong management layers with experienced Vice Presidents who can translate executive vision into operational excellence. These are the leaders who drive day-to-day performance and build the teams beneath them.',
  capabilities: ['VP of Engineering & Product', 'VP of Sales & Business Development', 'VP of Marketing & Growth', 'VP of People & Operations', 'VP of Finance & Strategy']
}, {
  icon: Building2,
  title: 'Board Advisory',
  subtitle: 'Independent Directors & Advisors',
  description: 'Connecting companies with independent board members who bring strategic value, governance expertise, and meaningful networks. The right board composition accelerates growth and strengthens investor confidence.',
  capabilities: ['Independent board director searches', 'Advisory board composition', 'Board diversity initiatives', 'Governance expertise matching', 'Industry-specific board talent']
}, {
  icon: Rocket,
  title: 'Startup Leadership',
  subtitle: 'Seed Stage Through Series A',
  description: "Specialized recruitment for early-stage companies making their first critical leadership hires. We know that at the seed and Series A stage, every hire is a bet on the company's future — we help founders make the right ones.",
  capabilities: ['First VP-level hires for founding teams', 'Technical co-founder matching', 'Go-to-market leadership placement', 'Scaling-stage executive recruitment', 'Founder-executive culture fit assessment']
}];
export function ServicesPage({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="min-h-screen bg-white pt-28 pb-20" data-id="element-219">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-220">
        {/* Back link */}
        <motion.button initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} onClick={onBack} className="flex items-center text-primary hover:text-primary-dark transition-colors mb-12 group" data-id="element-221">
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-222" />
          <span className="text-sm uppercase tracking-widest font-medium" data-id="element-223">
            Back to Home
          </span>
        </motion.button>

        {/* Page heading */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-20" data-id="element-224">
          <div className="w-16 h-1 bg-primary mb-8" data-id="element-225"></div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight mb-4" data-id="element-226">
            Our Expertise
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl" data-id="element-227">
            We specialize in finding the perfect match for high-stakes roles
            where leadership capability defines company success. Every search is
            conducted with the rigor, discretion, and urgency that critical
            hires demand.
          </p>
        </motion.div>

        {/* Service sections */}
        <div className="space-y-24" data-id="element-228">
          {services.map((service, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15 + index * 0.1
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start" data-id="element-229">
              {/* Icon + Title */}
              <div className="lg:col-span-4" data-id="element-230">
                <div className="flex items-center mb-4" data-id="element-231">
                  <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mr-4" data-id="element-232">
                    <service.icon className="w-7 h-7 text-primary" strokeWidth={1.5} data-id="element-233" />
                  </div>
                  <div data-id="element-234">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-235">
                      {service.title}
                    </h2>
                    <p className="text-sm text-primary font-medium uppercase tracking-wider" data-id="element-236">
                      {service.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description + Capabilities */}
              <div className="lg:col-span-8" data-id="element-237">
                <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-238">
                  {service.description}
                </p>
                <ul className="space-y-3" data-id="element-239">
                  {service.capabilities.map((cap, i) => <li key={i} className="flex items-center text-gray-600" data-id="element-240">
                      <CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" strokeWidth={1.5} data-id="element-241" />
                      {cap}
                    </li>)}
                </ul>
                {index < services.length - 1 && <div className="mt-12 border-b border-gray-100" data-id="element-242"></div>}
              </div>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="mt-24 bg-gray-50 p-10 md:p-14 text-center" data-id="element-243">
          <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-244">
            Ready to find your next leader?
          </h3>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-245">
            Every search begins with a conversation. Tell us about your needs
            and we'll outline how we can help.
          </p>
          <button onClick={() => {
          onBack();
        }} className="inline-flex items-center px-8 py-3 bg-primary text-white font-medium uppercase tracking-wider hover:bg-primary-dark transition-colors duration-200" data-id="element-246">
            Get in Touch
          </button>
        </motion.div>
      </div>

      {/* Testimonials section - full width */}
      <Testimonials data-id="element-247" />
    </div>;
}